/**
 * The two view pools of the unified visibility editor. `pool` is the DISPLAY
 * pool: the union of views, ordered and labelled by origin — this form type's
 * own views, then the org-wide ones, then everything defined elsewhere. Origin
 * `other` exists ONLY when the mount names a `formTypeId`: with no form type
 * there is no meaningful "own" set, so the whole union is contextual and every
 * entry reads `org`. Scoping a form-less surface to the org baseline instead
 * would leave it one selectable view, since a real org keeps everything but
 * `internal` and `pdfview` under `visibilityTypesByFormType` (SYD-2300).
 * `writePool` is the WRITE pool: every view the org defines minus `internal`,
 * identical at every mount and independent of `formTypeId`. It must never
 * narrow — `composeVisibilityCellWrite` materializes a hide as the explicit
 * complement, so a contextual write pool would silently hide the object in
 * every view it omits, and no later merge could restore ids that were never
 * stored. `internal` is excluded from both pools but stays reachable through
 * `viewById`, which spans the union so metadata lookups never fire a false
 * "not defined" warning. `getShownVisibilityTypes` / `mergeVisibilityTypes`
 * carry the retired `useVisibilityTypeSelection` contract verbatim: stored ids
 * outside the union survive every merge as locked entries. `error` is passed
 * straight through: when the `parameters/organization` listener fails the pool
 * silently degrades to the built-in defaults, so every surface that edits
 * through it must disable editing rather than compose a complement against a
 * pool that is not the org's.
 */
import { useCallback, useMemo } from 'react';
import type { VisibilityTypeId, VisibilityType } from '#types';
import { useOrganizationParameters } from '../../use/useOrganizationParameters';
import type { ViewPoolOrigin, ViewVisibilityPoolEntry } from './ViewVisibility.types';

export const useViewVisibilityPool = (formTypeId?: string) => {
    const { allVisibilityTypes, visibilityOptions, visibilityTypesByFormType, loading, error } =
        useOrganizationParameters();

    const viewById = useMemo((): Map<VisibilityTypeId, VisibilityType> => {
        return new Map(allVisibilityTypes.map((view) => [view.id, view]));
    }, [allVisibilityTypes]);

    /** Every selectable view in the organization. The ONLY sanctioned write pool. */
    const writePool = useMemo((): VisibilityTypeId[] => {
        return allVisibilityTypes.map((view) => view.id).filter((id) => id !== 'internal');
    }, [allVisibilityTypes]);

    const pool = useMemo((): ViewVisibilityPoolEntry[] => {
        const formTypeViews = formTypeId ? visibilityTypesByFormType[formTypeId] ?? [] : [];
        const orgBaselineIds = new Set(visibilityOptions.map((view) => view.id));
        const ownedIds = Array.from(new Set(formTypeViews.map((view) => view.id)))
            .filter((id) => id !== 'internal' && !orgBaselineIds.has(id));
        const orgIds = Array.from(orgBaselineIds).filter((id) => id !== 'internal');
        const placedIds = new Set([...ownedIds, ...orgIds]);
        const otherIds = writePool.filter((id) => !placedIds.has(id));
        const entriesOf = (ids: VisibilityTypeId[], origin: ViewPoolOrigin): ViewVisibilityPoolEntry[] =>
            ids
                .map((id) => viewById.get(id))
                .filter((view): view is VisibilityType => Boolean(view))
                .map((view) => ({ view, origin }));
        return [
            ...entriesOf(ownedIds, 'own'),
            ...entriesOf(orgIds, 'org'),
            ...entriesOf(otherIds, formTypeId ? 'other' : 'org'),
        ];
    }, [visibilityOptions, visibilityTypesByFormType, formTypeId, viewById, writePool]);

    const poolLabel = useMemo((): string => {
        const rendered = pool.filter((entry) => entry.origin !== 'other');
        const orgCount = rendered.filter((entry) => entry.origin === 'org').length;
        const formCount = rendered.filter((entry) => entry.origin === 'own').length;
        const parts = [orgCount > 0 ? `${orgCount} org` : '', formCount > 0 ? `${formCount} form` : ''];
        const named = parts.filter((part) => part.length > 0).join(' + ');
        if (named.length === 0) return 'No views';
        return `${named} ${rendered.length === 1 ? 'view' : 'views'}`;
    }, [pool]);

    /** Membership is union-wide, never contextual, so locked-id behavior stays independent of display. */
    const selectableViewIdSet = useMemo(() => new Set(writePool), [writePool]);

    const getShownVisibilityTypes = useCallback((types: VisibilityTypeId[] | undefined): VisibilityTypeId[] => {
        return (types ?? []).filter((id) => selectableViewIdSet.has(id));
    }, [selectableViewIdSet]);

    const mergeVisibilityTypes = useCallback((nextShownVisibilityTypes: VisibilityTypeId[], currentVisibilityTypes: VisibilityTypeId[] | undefined): VisibilityTypeId[] => {
        const lockedVisibilityTypes = (currentVisibilityTypes ?? []).filter((id) => !selectableViewIdSet.has(id));
        return Array.from(new Set([...nextShownVisibilityTypes, ...lockedVisibilityTypes]));
    }, [selectableViewIdSet]);

    return {
        pool,
        poolLabel,
        writePool,
        viewById,
        loading,
        error,
        getShownVisibilityTypes,
        mergeVisibilityTypes,
    };
};
